/* Reject → corrective task (Доручення).
 *
 * Restores the flow the redesign dropped: rejecting a photo does not just record a verdict, it
 * asks the merchandiser to fix the shelf. The rejection is posted only AFTER the task is created,
 * so a failed task never leaves a rejection with nothing attached to it — that ordering is from
 * the pre-redesign implementation and is the reason it is worth keeping.
 *
 * The payload is the old one, field for field (`git show HEAD:src/module/photo-review/View.js`):
 * `components: [3]` is the "photo" component, hardcoded there too because the API exposes no way
 * to look it up; `template` falls back to the account's default from the store.
 *
 * Shared by `/photo-review` and `/answers`. It takes the four things it actually needs rather than
 * a page's record — it used to read `item.picsell_info` and `item.photo[0]`, which is the photo
 * report's own shape and nothing else's:
 *
 * ```jsx
 * <RejectTaskModal
 *   target={{ objectId, objectType }}   // what the verdict is posted against
 *   photo={url}                         // what the annotator opens on; optional
 *   point={{ id, name, locality }}      // where the corrective task lands; optional
 *   visitId={n} lang={lang} onCancel={…} onDone={…} />
 * ```
 */
import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Alert as AntAlert, Button, Col, DatePicker, Flex, Form, Input, Modal, Row, Select, Typography } from "antd";

import Alert from "/src/util/alert";

import { PHOTO_REPORT, PHOTO_REVIEW, TRANSLATION_DOWNLOADER } from "../config/lang";
import { call } from "../util/action";
import dayjs from "../util/dayjs";
import { getStore } from "../util/redux/store";
import { translate, validateReviewTask } from "../util/util";

import EntitySelect from "./EntitySelect";
import PhotoAnnotator from "./PhotoAnnotator";

/** The "photo" task component. Hardcoded in the pre-redesign flow for want of a lookup endpoint. */
const PHOTO_COMPONENT_ID = 3;
/** How long the merchandiser gets, in days, before the task is due. */
const DEFAULT_DUE_DAYS = 30;

export default function RejectTaskModal({ target, photo, point, visitId, lang, onCancel, onDone }) {
  const me = useSelector(state => state.me);
  const t = (key, group = PHOTO_REPORT) => translate(group, key, lang);

  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [due, setDue] = useState(dayjs().add(DEFAULT_DUE_DAYS, "day"));
  const [priority, setPriority] = useState(2);
  /* The template decides what the merchandiser is asked to submit, so it is a choice, not a
     constant. `defaultTemplate` is only populated as a side effect of a legacy `EntityDropdown`
     for that entity having been mounted somewhere in the session — on `/photo-review` and
     `/answers` it is usually null, which `validateReviewTask` then rejects with "choose a
     template" and no control to do it with. It stays the seed when it is there. */
  const [template, setTemplate] = useState(getStore().getState().defaultTemplate || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [annotated, setAnnotated] = useState(false);
  const annotator = useRef(null);

  const onDirtyChange = useCallback(value => setAnnotated(value), []);

  /* The annotator exports from its own untransformed stage, so this gets the photo at its own
     resolution with the marks in place — not a screenshot of the zoomed viewport. */
  const exportAnnotated = () => (annotated ? annotator.current?.toDataURL() : null);

  /* The rejection itself. Posted last, so the merchandiser never sees a rejection with no task. */
  const postRejection = taskCreated => {
    call(
      {
        method: "POST",
        url: "approvals/",
        post: { object_id: target.objectId, object_type: target.objectType, is_approved: false, comment },
      },
      response => {
        setBusy(false);
        if (!response) {
          /* The task exists but the verdict did not land. Say so precisely — "failed" would send
             the reviewer back to create a second task for the same photo. */
          Alert.error(t("reject_task_saved_verdict_failed"));
          return;
        }
        Alert.success(taskCreated ? translate(TRANSLATION_DOWNLOADER, "newtask_success_msg", lang) : t("reject_done"));
        onDone(response);
      },
      {},
      true
    );
  };

  const createTask = imageId => {
    const post = {
      images: imageId ? [imageId] : [],
      company: null,
      title: title.trim(),
      start: dayjs().format("YYYY-MM-DDT00:00:00"),
      end: dayjs(due).format("YYYY-MM-DDT23:59:59"),
      tags: [],
      components: [PHOTO_COMPONENT_ID],
      priority,
      template: template && typeof template === "object" ? template.id ?? template.value : template,
      point: point?.id || null,
      network: null,
      created_by: me?.id || null,
      visit_id: visitId || 0,
    };

    const { validate, errors } = validateReviewTask("photo-review", post);
    if (!validate) {
      setBusy(false);
      setError(Object.values(errors)[0]);
      return;
    }

    call({ method: "POST", url: "tasks/", post }, response => {
      if (!response?.id) {
        setBusy(false);
        setError(translate(TRANSLATION_DOWNLOADER, "valid_error_msg", lang));
        return;
      }
      postRejection(true);
    });
  };

  const submit = () => {
    if (!target?.objectId || !target?.objectType) {
      setError(translate(TRANSLATION_DOWNLOADER, "task_object_item_id_msg", lang));
      return;
    }
    setError(null);
    setBusy(true);

    const marked = exportAnnotated();
    if (!marked) {
      /* No marks: the task carries the original photo. `images/` accepts a URL-sourced upload
         through the same endpoint, so there is nothing special to do — send none and let the task
         reference the visit. */
      createTask(null);
      return;
    }
    call({ method: "POST", url: "images/", post: { owner: me, image: marked } }, image => {
      if (!image?.id) {
        setBusy(false);
        setError(translate(TRANSLATION_DOWNLOADER, "valid_error_msg", lang));
        return;
      }
      createTask(image.id);
    });
  };

  return (
    <Modal
      open
      title={t("reject_task_title")}
      width={1240}
      centered
      rootClassName="rtm"
      onCancel={busy ? undefined : onCancel}
      footer={
        <Flex align="center" justify="space-between" gap="small">
          {/* Rejecting without a task stays possible — a blurred photo needs no shelf work. */}
          <Button type="text" disabled={busy} onClick={() => (setBusy(true), postRejection(false))}>
            {t("reject_without_task")}
          </Button>
          <Flex gap="small">
            <Button disabled={busy} onClick={onCancel}>
              {translate(TRANSLATION_DOWNLOADER, "cancel", lang)}
            </Button>
            <Button type="primary" danger loading={busy} onClick={submit}>
              {t("reject_task_submit")}
            </Button>
          </Flex>
        </Flex>
      }>
      {/* Side by side: the photo is bounded by its own column instead of setting the modal's
          height, so the form is visible without scrolling. Stacks below `lg`. */}
      <Row gutter={[16, 16]} className="rtm__body">
        <Col xs={24} lg={14}>
          {photo ? <PhotoAnnotator ref={annotator} src={photo} lang={lang} onDirtyChange={onDirtyChange} /> : null}
        </Col>
        <Col xs={24} lg={10}>
          <Form layout="vertical" component={false} colon={false}>
            <Flex vertical gap="small">
              <Form.Item label={t("reject_task_name")} required>
                <Input value={title} maxLength={120} onChange={event => setTitle(event.target.value)} />
              </Form.Item>
              {/* What the merchandiser is asked to submit. `seed` is only useful when the store's
                  default is a full object; an id resolves itself off the first page of options. */}
              <Form.Item label={t("reject_task_template")} required>
                <EntitySelect entity="template" value={template} onChange={setTemplate} allowClear={false} />
              </Form.Item>
              <Flex gap="small" wrap>
                <Form.Item label={t("reject_task_due")} className="rtm__due">
                  <DatePicker value={due} allowClear={false} disabledDate={d => d && d < dayjs().startOf("day")} onChange={setDue} />
                </Form.Item>
                <Form.Item label={t("reject_task_priority")} className="rtm__priority">
                  <Select value={priority} onChange={setPriority} options={[1, 2, 3].map(value => ({ value, label: t(`priority_${value}`) }))} />
                </Form.Item>
              </Flex>
              <Form.Item label={translate(PHOTO_REVIEW, "comment", lang)}>
                <Input.TextArea value={comment} rows={3} maxLength={500} onChange={event => setComment(event.target.value)} />
              </Form.Item>

              {point?.name ? (
                <Typography.Text type="secondary">
                  {t("reject_task_for")}: {[point.locality?.name, point.name].filter(Boolean).join(" · ")}
                </Typography.Text>
              ) : null}

              {error ? <AntAlert type="error" showIcon message={error} /> : null}
            </Flex>
          </Form>
        </Col>
      </Row>
    </Modal>
  );
}
